import { useLocation, useHistory } from "react-router-dom";
import PageContainer from "../../../components/PageContainer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MdOutlineArrowBack } from "react-icons/md";
import FormatDataService from "../../../services/util/FormatData";
import Loading from "../../../components/Loading";
import { Alert, Button, Dropdown, Spinner, Table } from "react-bootstrap";
import RelatorioApiService from "../../../services/api/RelatorioApiService";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FiInfo } from "react-icons/fi";

const popupStyle = {
    position: "absolute",
    top: "-40%",
    left: "-600%",
    transform: "translateX(-82%)",
    backgroundColor: "#e9e9e9",
    color: "#212529",
    border: "1px solid #dee2e6",
    borderRadius: "5px",
    padding: "5px 10px",
    zIndex: 1000,
    whiteSpace: "nowrap",
    boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
    width: "auto",
    fontWeight: "normal",
};

function parseCompTimeToMinutes(compTime) {
    if (compTime === null || compTime === undefined) return 0;
    const hours = Number(compTime) || 0;
    return Math.round(hours * 60);
}

function minutesToCompTime(totalMinutes) {
    const isNeg = totalMinutes < 0;
    const abs = Math.abs(totalMinutes);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `${isNeg ? "-" : ""}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function RelatorioBancoDeHorasView() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [bancoDeHorasData, setBancoDeHorasData] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: "FullName", direction: "asc" });
    const [isExporting, setIsExporting] = useState(false);
    const [visiblePeriodoTooltip, setVisiblePeriodoTooltip] = useState(null);
    const [selectedGestor, setSelectedGestor] = useState(null);

    const location = useLocation();
    const history = useHistory();
    const { data } = location.state || { data: { periodoInicial: "", periodoFinal: "" } };
    const { periodoInicial, periodoFinal } = data;

    const formatPeriodoFull = useCallback(
        (periodo) => FormatDataService.formatarPeriodoPorExtenso(periodo),
        []
    );

    /* FETCH */
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const dataInicial = FormatDataService.formatarDataParaYYYYMM(periodoInicial);
            const dataFinal = FormatDataService.formatarDataParaYYYYMM(periodoFinal);

            const response = await RelatorioApiService.getBancoDeHoras(dataInicial, dataFinal);
            setBancoDeHorasData(response?.data?.result ?? {});
        } catch (err) {
            console.error("Erro ao carregar relatório:", err);
            setError("Erro ao carregar dados do relatório. Tente novamente.");
        } finally {
            setLoading(false);
        }
    }, [periodoInicial, periodoFinal]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /* MODELAGEM: um registro por colaborador com CompTime somado por período */
    const { colaboradoresPorPeriodo, periodosOrdenados } = useMemo(() => {
        if (!bancoDeHorasData?.grouped) {
            return { colaboradoresPorPeriodo: {}, periodosOrdenados: [] };
        }

        const periodos = Object.keys(bancoDeHorasData.grouped)
            .filter((key) => /^\d{4}-\d{2}$/.test(key))
            .sort((a, b) => Number(a.replace("-", "")) - Number(b.replace("-", "")));

        const byEmployee = {};

        for (const periodo of periodos) {
            const arr = bancoDeHorasData.grouped[periodo]?.items ?? [];

            for (const item of arr) {
                const empNum = item?.EmployeeNumber;
                const fullName = item?.FullName ?? "Não informado";
                // Chave composta evita colisão quando EmployeeNumber é nulo
                // ou quando dois colaboradores distintos compartilham o mesmo número
                const key = empNum != null ? String(empNum) : `nonum_${fullName}`;

                if (!byEmployee[key]) {
                    byEmployee[key] = {
                        EmployeeNumber: empNum ?? "N/A",
                        FullName: fullName,
                        Manager: item?.Manager ?? "Não informado",
                        Email: item?.Email ?? "",
                        demitido: item?.demitido ?? false,
                        periodos: {},
                    };
                }

                if (!byEmployee[key].periodos[periodo]) {
                    byEmployee[key].periodos[periodo] = { compTimeMinutes: 0, valorBanco50Total: 0 };
                }

                byEmployee[key].periodos[periodo].compTimeMinutes += parseCompTimeToMinutes(item?.CompTime);
                byEmployee[key].periodos[periodo].valorBanco50Total += Number(item?.ValorBanco50Pagar?.total) || 0;
            }

            for (const key of Object.keys(byEmployee)) {
                if (byEmployee[key].periodos[periodo] === undefined) {
                    byEmployee[key].periodos[periodo] = { compTimeMinutes: 0, valorBanco50Total: 0 };
                }
            }
        }

        return { colaboradoresPorPeriodo: byEmployee, periodosOrdenados: periodos };
    }, [bancoDeHorasData]);

    /* Lista ordenada */
    const sortedColaboradores = useMemo(() => {
        const list = Object.values(colaboradoresPorPeriodo);
        const key = sortConfig.key;
        const dir = sortConfig.direction === "asc" ? 1 : -1;

        return [...list].sort((a, b) => {
            const aVal = (key === "FullName" ? a.FullName : a.Manager) ?? "";
            const bVal = (key === "FullName" ? b.FullName : b.Manager) ?? "";
            return aVal.toLowerCase().localeCompare(bVal.toLowerCase()) * dir;
        });
    }, [colaboradoresPorPeriodo, sortConfig]);



    /* Lista de gestores únicos */
    const gestores = useMemo(() => {
        const set = new Set(
            Object.values(colaboradoresPorPeriodo)
                .map((c) => c.Manager)
                .filter(Boolean)
        );
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [colaboradoresPorPeriodo]);

    /* Colaboradores filtrados */
    const filteredColaboradores = useMemo(() => {
        if (!selectedGestor) return sortedColaboradores;
        return sortedColaboradores.filter((c) => c.Manager === selectedGestor);
    }, [sortedColaboradores, selectedGestor]);

    /* Totais por período baseados nos colaboradores filtrados */
    const totaisFiltradosPorPeriodo = useMemo(() => {
        const result = {};
        for (const periodo of periodosOrdenados) {
            let totalMinutes = 0;
            for (const colab of filteredColaboradores) {
                totalMinutes += colab.periodos[periodo]?.compTimeMinutes ?? 0;
            }
            result[periodo] = { compTimeMinutes: totalMinutes };
        }
        return result;
    }, [filteredColaboradores, periodosOrdenados]);

    /* Total geral filtrado */
    const totalGeralFiltrado = useMemo(() => {
        return periodosOrdenados.reduce(
            (acc, periodo) => acc + (totaisFiltradosPorPeriodo[periodo]?.compTimeMinutes ?? 0),
            0
        );
    }, [totaisFiltradosPorPeriodo, periodosOrdenados]);

    /* Períodos a destacar em amarelo: mês atual e mês anterior, sempre */
    const periodosParaDestacar = useMemo(() => {
        const hoje = new Date();
        const ano = hoje.getFullYear();
        const mes = hoje.getMonth();
        const formatPeriodo = (date) =>
            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

        const mesAtual = formatPeriodo(new Date(ano, mes, 1));
        const mesAnterior = formatPeriodo(new Date(ano, mes - 1, 1));

        const periodosNoFiltro = new Set(periodosOrdenados);

        return [mesAnterior, mesAtual].filter((p) => periodosNoFiltro.has(p));
    }, [periodosOrdenados]);

    const setPeriodosDestacar = useMemo(
        () => new Set(periodosParaDestacar),
        [periodosParaDestacar]
    );

    const gestorTooltipText = periodosOrdenados.length === 1
        ? "Gestor vigente no mês selecionado"
        : "Gestor atual do colaborador";

    /* SORT */
    const handleSort = useCallback((key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    }, []);

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) return <span style={{ opacity: 0.3 }}>▴</span>;
        return <span>{sortConfig.direction === "asc" ? "▴" : "▾"}</span>;
    };

    /* EXPORT */
    const exportDataInExcel = useCallback(async () => {
        setIsExporting(true);

        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Banco de Horas");

            const yellowFill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFF4CC" },
            };

            const headerCols = [
                "Colaborador",
                "Gestor",
                ...periodosOrdenados.flatMap((p) => {
                    const label = formatPeriodoFull(p);
                    return [`${label} Banco de Horas`, `${label} Valor (R$)`];
                }),
            ];

            const headerRow = worksheet.addRow(headerCols);
            headerRow.eachCell((cell, colNumber) => {
                cell.font = { bold: true };
                if (colNumber >= 3) {
                    const periodoIdx = Math.floor((colNumber - 3) / 2);
                    if (
                        periodosOrdenados[periodoIdx] &&
                        setPeriodosDestacar.has(periodosOrdenados[periodoIdx])
                    ) {
                        cell.fill = yellowFill;
                    }
                }
            });

            filteredColaboradores.forEach((row) => {
                const periodoCols = periodosOrdenados.flatMap((periodo) => {
                    const periodoData = row.periodos[periodo] ?? { compTimeMinutes: 0, valorBanco50Total: 0 };
                    return [
                        minutesToCompTime(periodoData.compTimeMinutes),
                        periodoData.valorBanco50Total
                            ? periodoData.valorBanco50Total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : "—",
                    ];
                });

                const excelRow = worksheet.addRow([
                    row.FullName,
                    row.Manager,
                    ...periodoCols,
                ]);

                if (row.demitido) {
                    excelRow.getCell(1).font = { strike: true };
                }

                excelRow.eachCell((cell, colNumber) => {
                    if (colNumber >= 3) {
                        const periodoIdx = Math.floor((colNumber - 3) / 2);
                        if (
                            periodosOrdenados[periodoIdx] &&
                            setPeriodosDestacar.has(periodosOrdenados[periodoIdx])
                        ) {
                            cell.fill = yellowFill;
                        }
                    }
                });
            });

            const gestorNoNomeArquivo =
                selectedGestor && selectedGestor !== "Todos" ? ` - ${selectedGestor}` : "";

            const inicialFormatadoExport = formatPeriodoFull(periodoInicial);
            const finalFormatadoExport = formatPeriodoFull(periodoFinal);
            const nomeArquivo =
                inicialFormatadoExport !== "Data inválida" && finalFormatadoExport !== "Data inválida"
                    ? `Relatório Banco de Horas${gestorNoNomeArquivo} - ${inicialFormatadoExport} a ${finalFormatadoExport}`
                    : `Relatório Banco de Horas${gestorNoNomeArquivo}`;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
            });
            saveAs(blob, `${nomeArquivo}.xlsx`);
        } catch (err) {
            console.error("Erro ao exportar:", err);
            setError("Erro ao exportar dados. Tente novamente.");
        } finally {
            setIsExporting(false);
        }
    }, [filteredColaboradores, periodosOrdenados, periodoInicial, periodoFinal, formatPeriodoFull, setPeriodosDestacar]);

    /* Render */
    const hasData = sortedColaboradores.length > 0;

    useEffect(() => {
        if (loading || error || !hasData) {
            return undefined;
        }
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [loading, error, hasData]);

    const actions = [
        {
            label: "Voltar",
            icon: <MdOutlineArrowBack />,
            color: "info",
            type: "button",
            onClick: () => history.goBack(),
        },
    ];

    const inicialFormatado = formatPeriodoFull(periodoInicial);
    const finalFormatado = formatPeriodoFull(periodoFinal);
    const title =
        inicialFormatado !== "Data inválida" && finalFormatado !== "Data inválida"
            ? `Histórico de Banco de Horas - ${inicialFormatado} a ${finalFormatado}`
            : "Histórico de Banco de Horas";

    return (
        <PageContainer title={title} actions={actions}>
            {loading && <Loading message="Carregando..." />}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {!loading && !error && !hasData && (
                <Alert variant="primary">
                    Nenhum dado disponível para o período selecionado.
                </Alert>
            )}

            {!loading && !error && hasData && (
                <div className="banco-de-horas-scroll-root">
                    <div className="d-flex mb-3 banco-de-horas-toolbar align-items-center" style={{ gap: "0.5rem" }}>
                        <Button onClick={exportDataInExcel} disabled={isExporting}>
                            {isExporting ? (
                                <>
                                    <Spinner size="sm" animation="border" className="mr-2" />
                                    Exportando...
                                </>
                            ) : (
                                "Exportar"
                            )}
                        </Button>
                        <Dropdown>
                            <Dropdown.Toggle variant="light" id="dd-gestor">
                                Filtrar por Gestor:{" "}
                                <span style={{ fontWeight: "bold" }}>
                                    {selectedGestor ?? "Todos"}
                                </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ maxHeight: 300, overflowY: "auto" }}>
                                <Dropdown.Item onClick={() => setSelectedGestor(null)}>
                                    Todos
                                </Dropdown.Item>
                                {gestores.map((g) => (
                                    <Dropdown.Item key={g} onClick={() => setSelectedGestor(g)}>
                                        {g}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    <style>{`
                        .banco-de-horas-scroll-root {
                            display: flex;
                            flex-direction: column;
                            height: calc(100dvh - var(--header-height) - 1.7rem);
                            min-height: 240px;
                            overflow: hidden;
                        }
                        .banco-de-horas-scroll-root > .banco-de-horas-toolbar,
                        .banco-de-horas-scroll-root > .banco-de-horas-totais {
                            flex-shrink: 0;
                        }
                        .banco-de-horas-totais {
                            overflow-x: auto;
                            max-width: calc(100vw - 265px);
                        }
                        .banco-de-horas-scroll-root .table-responsive-custom {
                            flex: 1;
                            min-height: 0;
                            overflow-x: auto;
                            overflow-y: auto;
                            max-width: calc(100vw - 265px);
                        }
                        .colaborador-sticky {
                            position: sticky !important;
                            left: -0.25px;
                            z-index: 3;
                            border: 1px solid #dee2e6;
                        }
                        .colaborador-sticky::after {
                            content: '';
                            position: absolute;
                            top: 0;
                            right: -3px;
                            width: 6px;
                            height: 100%;
                            background: linear-gradient(to right, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0));
                            z-index: 9;
                            pointer-events: none;
                        }
                        th.colaborador-sticky {
                            z-index: 4;
                            background-color: #f5f5f5 !important;
                        }
                        .table-striped > tbody > tr:nth-of-type(odd) > td.colaborador-sticky {
                            background-color: #e9e9e9 !important;
                        }
                        .table-striped > tbody > tr:nth-of-type(even) > td.colaborador-sticky {
                            background-color: #f5f5f5 !important;
                        }
                        .table-hover > tbody > tr:nth-of-type(even):hover > td.colaborador-sticky {
                            background-color: rgb(233, 233, 233) !important;
                        }
                        .table-hover > tbody > tr:nth-of-type(odd):hover > td.colaborador-sticky {
                            background-color: rgb(228, 228, 228) !important;
                        }
                        @media (max-width: 1200px) {
                            .banco-de-horas-scroll-root .table-responsive-custom,
                            .banco-de-horas-totais {
                                max-width: calc(100vw - 230px);
                            }
                        }
                        @media (max-width: 768px) {
                            .banco-de-horas-scroll-root .table-responsive-custom,
                            .banco-de-horas-totais {
                                max-width: 100vw;
                            }
                            .banco-de-horas-scroll-root {
                                height: calc(100dvh - var(--header-height) - 10rem);
                            }
                        }
                        th.periodo-destacado,
                        td.periodo-destacado {
                            background-color: rgb(255, 244, 204) !important;
                        }
                        .table-banco-de-horas {
                            border-collapse: separate;
                            border-spacing: 0;
                        }
                        .table-banco-de-horas thead th {
                            position: sticky;
                            top: 0;
                            z-index: 5;
                            background-color: #f5f5f5;
                            box-shadow: inset 0 -1px 0 #dee2e6;
                        }
                        .table-banco-de-horas thead th.colaborador-sticky {
                            z-index: 7;
                            background-color: #f5f5f5 !important;
                            box-shadow: inset 0 -1px 0 #dee2e6, inset -1px 0 0 #dee2e6;
                        }
                        .table-banco-de-horas thead th.periodo-destacado {
                            background-color: rgb(255, 244, 204) !important;
                        }
                        td:hover .banco-horas-tooltip {
                            opacity: 1 !important;
                            visibility: visible !important;
                        }
                        td:hover .gestor-tooltip {
                            opacity: 1 !important;
                            visibility: visible !important;
                        }
                    `}</style>

                    <div className="w-100 mb-4 banco-de-horas-totais">
                        <Table bordered style={{ background: "#e1eaf3" }}>
                            <thead>
                                <tr>
                                    <th></th>
                                    {periodosOrdenados.map((periodo) => (
                                        <th
                                            key={periodo}
                                            className={setPeriodosDestacar.has(periodo) ? "periodo-destacado text-nowrap" : "text-nowrap"}
                                            style={{ whiteSpace: "nowrap", textAlign: "center" }}
                                        >
                                            {formatPeriodoFull(periodo)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="text-nowrap"><b>Total de banco</b></td>
                                    {periodosOrdenados.map((periodo) => {
                                        const minutes = totaisFiltradosPorPeriodo[periodo]?.compTimeMinutes ?? 0;
                                        const display = minutesToCompTime(minutes);
                                        const isDestacado = setPeriodosDestacar.has(periodo);
                                        return (
                                            <td
                                                key={periodo}
                                                className={isDestacado ? "periodo-destacado text-nowrap" : "text-nowrap"}
                                                style={{ textAlign: "center" }}
                                            >
                                                <b>{display}</b>
                                            </td>
                                        );
                                    })}
                                </tr>
                                <tr>
                                <td className="text-nowrap"><b>Valor do banco</b></td>
                                {periodosOrdenados.map((periodo) => {
                                    // buscar o valor total de reais do mês somando o valorBanco50Total de todos colaboradores filtrados
                                    const valorBanco50TotalPeriodo = filteredColaboradores.reduce((soma, colaborador) => {
                                        const periodoData = colaborador.periodos?.[periodo];
                                        const valor = periodoData && periodoData.valorBanco50Total
                                            ? Number(periodoData.valorBanco50Total)
                                            : 0;
                                        return soma + valor;
                                    }, 0);
                                    return (
                                        <td
                                            key={`valor-${periodo}`}
                                            className={setPeriodosDestacar.has(periodo) ? "periodo-destacado text-nowrap" : "text-nowrap"}
                                            style={{ textAlign: "center" }}
                                        >
                                            <span>
                                                <b>
                                                    {valorBanco50TotalPeriodo.toLocaleString("pt-BR", {
                                                        style: "currency",
                                                        currency: "BRL",
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}
                                                </b>
                                            </span>
                                        </td>
                                    );
                                })}
                                </tr>
                            </tbody>
                        </Table>
                    </div>

                    <div className="table-responsive-custom">
                        <Table striped bordered hover className="table-banco-de-horas">
                            <thead>
                                <tr>
                                    <th
                                        role="button"
                                        className="colaborador-sticky"
                                        style={{ whiteSpace: "nowrap" }}
                                        onClick={() => handleSort("FullName")}
                                    >
                                        Colaborador <SortIcon column="FullName" />
                                    </th>
                                    <th
                                        role="button"
                                        style={{ whiteSpace: "nowrap" }}
                                        onClick={() => handleSort("Manager")}
                                    >
                                        Gestor <SortIcon column="Manager" />
                                    </th>
                                    {periodosOrdenados.map((periodo) => {
                                        const labelPeriodo = formatPeriodoFull(periodo);
                                        const isDestacado = setPeriodosDestacar.has(periodo);

                                        const headerContent = isDestacado ? (
                                            <>
                                                {labelPeriodo}
                                                <span
                                                    onMouseEnter={() => setVisiblePeriodoTooltip(periodo)}
                                                    onMouseLeave={() => setVisiblePeriodoTooltip(null)}
                                                    className="ml-1 info-icon"
                                                    style={{ position: "relative", cursor: "pointer" }}
                                                >
                                                    <FiInfo />
                                                    {visiblePeriodoTooltip === periodo && (
                                                        <div className="hover-popup" style={popupStyle}>
                                                            Banco de Horas em processamento
                                                        </div>
                                                    )}
                                                </span>
                                            </>
                                        ) : (
                                            labelPeriodo
                                        );

                                        return (
                                            <th
                                                key={periodo}
                                                className={`col-periodo ${isDestacado ? "periodo-destacado" : ""}`}
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    textAlign: "center",
                                                    minWidth: 140,
                                                }}
                                            >
                                                {headerContent}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredColaboradores.map((colab) => (
                                    <tr key={colab.EmployeeNumber}>
                                        <td className="colaborador-sticky" style={{ whiteSpace: "nowrap" }}>
                            <span style={colab.demitido ? { textDecoration: "line-through" } : undefined}>
                                {colab.FullName}
                            </span>
                        </td>
                                        <td style={{ whiteSpace: "nowrap", position: "relative" }}>
                                            {colab.Manager}
                                            <div
                                                className="gestor-tooltip"
                                                style={{
                                                    position: "absolute",
                                                    bottom: "calc(100% + 1px)",
                                                    left: "20px",
                                                    transform: "none",
                                                    color: "#212529",
                                                    background: "#e9ecef",
                                                    border: "1px solid #000",
                                                    borderRadius: "5px",
                                                    padding: "5px 10px",
                                                    zIndex: 1000,
                                                    whiteSpace: "nowrap",
                                                    boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                                                    fontWeight: "normal",
                                                    opacity: 0,
                                                    visibility: "hidden",
                                                    transition: "opacity 0.15s",
                                                }}
                                            >
                                                {gestorTooltipText}
                                            </div>
                                        </td>
                                        {periodosOrdenados.map((periodo) => {
                                            const periodoData = colab.periodos[periodo] ?? { compTimeMinutes: 0, valorBanco50Total: 0 };
                                            const minutes = periodoData.compTimeMinutes;
                                            const valorBanco50Total = periodoData.valorBanco50Total;
                                            const display = minutesToCompTime(minutes);
                                            const isDestacado = setPeriodosDestacar.has(periodo);
                                            return (
                                                <td
                                                    key={periodo}
                                                    className={`col-periodo ${isDestacado ? "periodo-destacado" : ""}`}
                                                    style={{ textAlign: "center", whiteSpace: "nowrap", position: "relative" }}
                                                >
                                                    <span>{display}</span>
                                                        <div
                                                            className="banco-horas-tooltip"
                                                            style={{
                                                                position: "absolute",
                                                                bottom: "calc(100% + 4px)",
                                                                left: "50%",
                                                                transform: "translateX(-50%)",
                                                                color: "#212529",
                                                                background: "#e9ecef",
                                                                border: "1px solid #000",
                                                                borderRadius: "5px",
                                                                padding: "5px 10px",
                                                                zIndex: 1000,
                                                                whiteSpace: "nowrap",
                                                                boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                                                                fontWeight: "normal",
                                                                opacity: 0,
                                                                visibility: "hidden",
                                                                transition: "opacity 0.15s",
                                                            }}
                                                        >
                                                            <b>{`R$ ${valorBanco50Total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</b>
                                                        </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                                <tr>
                                    <td className="colaborador-sticky"><b>Total:</b></td>
                                    <td></td>
                                    {periodosOrdenados.map((periodo) => {
                                        const minutes = totaisFiltradosPorPeriodo[periodo]?.compTimeMinutes ?? 0;
                                        const display = minutesToCompTime(minutes);
                                        const isDestacado = setPeriodosDestacar.has(periodo);
                                        return (
                                            <td
                                                key={periodo}
                                                className={`col-periodo ${isDestacado ? "periodo-destacado" : ""}`}
                                                style={{ textAlign: "center", whiteSpace: "nowrap" }}
                                            >
                                                <b>{display}</b>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                </div>
            )}
        </PageContainer>
    );
}

export default RelatorioBancoDeHorasView;
